/**
 * The keyboard model of the limit cell (masterplan credit-limits-v3, B4; E14, E11, E7) as a pure state machine, so the
 * tests drive it without a DOM.
 *
 * Source: budget-app-v2 `web/src/components/grid/gridKeyboard.ts` @ `307a69c` (2026-09-29, read 2026-10-23), with its
 * test `web/src/__tests__/gridKeyboard.test.ts`. Kept from it: the open cell with a draft, Enter opens and Enter saves
 * through a dialog, Esc discards and keeps the selection, a digit or a point opens the cell and starts typing,
 * a locked cell never opens (`guardLocked`), `keyFromEvent` leaves browser shortcuts alone. What differs, and why:
 *
 * - **No cell selection, no arrows, no Tab walk.** `ClimGrid` already moves the focus between rows (roving tab stop)
 *   and the overview has exactly one writable column, the limit. The model therefore knows one cell per row, named by
 *   the row's key, and the grid tells it which row is focused.
 * - **Every save goes through the dialog.** A limit change creates a request with a mandatory reason (E6, E11), so
 *   there is no Ctrl+Enter shortcut past it and no Tab-out save; Tab in an open cell is left to the browser and the
 *   editor's blur discards.
 * - **No value letters (`y b r l`) and no history key**: the budget's shortcuts have no meaning for a credit limit.
 * - **`parseLimitInput`** turns the draft into a CHF amount the API accepts (`workflow.ts` `isValidLimit`).
 */

/** The open cell: the row's key and the text typed so far. */
export interface CellEditing {
    rowKey: string;
    draft: string;
}

export interface CellEditState {
    editing: CellEditing | null;
}

export type CellEditKey =
    | { key: 'Enter'; ctrl: boolean }
    | { key: 'Escape' }
    | { key: 'char'; char: string };

/** What the caller must do after a transition; the state itself is returned beside it. */
export type CellEditEffect =
    | { type: 'none' }
    | { type: 'open'; rowKey: string; initial: string }
    /** Enter in an open cell: show the request dialog with `draft` (mandatory reason, chain preview). */
    | { type: 'save'; rowKey: string; draft: string }
    | { type: 'discard'; rowKey: string };

export const CLOSED: CellEditState = { editing: null };

/** A limit is never negative, so unlike the budget grid a minus does not start typing. */
const TYPING_START = /^[0-9.]$/;

/**
 * One key. `focused` is the row the key was pressed on (null = the key came from the open editor), `locked` says
 * whether that row's limit cell may not be edited (no role, open request, E7/E11).
 */
export function transition(
    state: CellEditState,
    key: CellEditKey,
    focused: { rowKey: string; locked: boolean } | null,
): { state: CellEditState; effect: CellEditEffect } {
    const none = { state, effect: { type: 'none' } as CellEditEffect };

    if (state.editing) {
        const { rowKey, draft } = state.editing;
        if (key.key === 'Enter') return { state: CLOSED, effect: { type: 'save', rowKey, draft } };
        if (key.key === 'Escape') return { state: CLOSED, effect: { type: 'discard', rowKey } };
        return none;
    }

    if (!focused || focused.locked) return none;
    if (key.key === 'Enter') {
        return { state: { editing: { rowKey: focused.rowKey, draft: '' } }, effect: { type: 'open', rowKey: focused.rowKey, initial: '' } };
    }
    if (key.key === 'char' && TYPING_START.test(key.char)) {
        const initial = key.char;
        return { state: { editing: { rowKey: focused.rowKey, draft: initial } }, effect: { type: 'open', rowKey: focused.rowKey, initial } };
    }
    return none;
}

/** The mouse (double-click, the pencil): open the row's cell unless it is locked or another cell is open. */
export function openCell(state: CellEditState, rowKey: string, locked: boolean, initial = ''): CellEditState {
    if (state.editing || locked) return state;
    return { editing: { rowKey, draft: initial } };
}

/** The caller typed into the open cell. */
export function setDraft(state: CellEditState, draft: string): CellEditState {
    if (!state.editing) return state;
    return { editing: { ...state.editing, draft } };
}

/** Rows changed (filter, reload): an open cell whose row is gone closes. */
export function keepIfPresent(state: CellEditState, present: (rowKey: string) => boolean): CellEditState {
    return state.editing && !present(state.editing.rowKey) ? CLOSED : state;
}

/** A key event to the model's vocabulary; null when the cell should not handle it. */
export function keyFromEvent(e: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): CellEditKey | null {
    if (e.altKey) return null;
    if (e.key === 'Enter') return { key: 'Enter', ctrl: e.ctrlKey || e.metaKey };
    if (e.key === 'Escape') return { key: 'Escape' };
    if (e.ctrlKey || e.metaKey) return null;
    return e.key.length === 1 ? { key: 'char', char: e.key } : null;
}

// ---------------------------------------------------------------- the amount

/** `workflow.ts` MAX_LIMIT_CHF: far above any real limit, inside DECIMAL(19,2). */
export const MAX_LIMIT_CHF = 1_000_000_000;

export type ParsedLimit = { ok: true; value: number } | { ok: false; error: string };

/** The hint for every grouping the cell does not read unambiguously (Roman's review B1–B4, 2026-10-23). */
export const THOUSANDS_HINT = "Write thousands with an apostrophe or without a separator, e.g. 50'000 or 50000.";

/** Whole francs: plain digits, or groups of three after an apostrophe («50'000», «50’000», «1'250'000»). */
const INTEGER_PART = /^(?:\d{1,3}(?:['’]\d{3})+|\d+)$/;

/**
 * The typed text as a CHF amount: 50000, 50'000, 50’000, 50000.00; a decimal point with at most two decimals,
 * 0 up to 1'000'000'000 (0 or 1 = «no limit», E20). Everything that could be read two ways is refused with
 * `THOUSANDS_HINT` instead of guessing: a comma («50,000» is 50 thousand in English and 50 in German), a space, a
 * point followed by three digits («50.000»), more than one point, an apostrophe outside groups of three. The server
 * checks the amount again.
 */
export function parseLimitInput(text: string): ParsedLimit {
    const t = text.trim();
    if (t === '') return { ok: false, error: 'Enter the new limit in CHF.' };
    if (t.startsWith('-')) return { ok: false, error: 'The limit cannot be negative.' };
    if (/[,\s_]/.test(t) || (t.match(/\./g) ?? []).length > 1) return { ok: false, error: THOUSANDS_HINT };
    const [whole = '', decimals] = t.split('.');
    if (decimals !== undefined && !/^\d+$/.test(decimals)) return { ok: false, error: 'Enter a number, e.g. 50000 or 50\'000.' };
    if (!INTEGER_PART.test(whole)) {
        return /['’]/.test(whole) ? { ok: false, error: THOUSANDS_HINT } : { ok: false, error: 'Enter a number, e.g. 50000 or 50\'000.' };
    }
    if (decimals !== undefined && decimals.length === 3 && !/['’]/.test(whole)) return { ok: false, error: THOUSANDS_HINT };
    if (decimals !== undefined && decimals.length > 2) return { ok: false, error: 'At most two decimals.' };
    const value = Number(`${whole.replace(/['’]/g, '')}${decimals !== undefined ? `.${decimals}` : ''}`);
    if (!Number.isFinite(value)) return { ok: false, error: 'Enter a number, e.g. 50000 or 50\'000.' };
    if (value > MAX_LIMIT_CHF) return { ok: false, error: 'The limit is too high.' };
    return { ok: true, value: Math.round(value * 100) / 100 };
}

/** A limit <= 1 means «no limit» (masterplan §2, `workflow.ts` NO_LIMIT_MAX_CHF). */
export const NO_LIMIT_MAX_CHF = 1;

/**
 * The current limit as the API compares it (`createRequest`: NULL or negative counts as 0, two decimals).
 */
export function currentLimitForCompare(creditLimitChf: number | null): number {
    return Math.round(Math.max(0, creditLimitChf ?? 0) * 100) / 100;
}

/**
 * Would the API refuse this amount with 400 `NO_CHANGE` (`workflow.ts` `isSameLimit`)? The same amount, or «no limit»
 * to «no limit»: both <= 1 count as equal (0 -> 1 changes nothing, E20). The dialog says so before sending.
 */
export function isSameLimit(requestedChf: number, creditLimitChf: number | null): boolean {
    const current = currentLimitForCompare(creditLimitChf);
    if (requestedChf <= NO_LIMIT_MAX_CHF && current <= NO_LIMIT_MAX_CHF) return true;
    return Math.round(requestedChf * 100) === Math.round(current * 100);
}
